import React, { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

/**
 * Reusable date picker built on shadcn Calendar + Popover.
 * Emits ISO date strings (YYYY-MM-DD) via onChange.
 */
export default function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  weekStartsOn = 1, // Monday
  testId = "date-picker",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
  const selected = parsed && isValid(parsed) ? parsed : undefined;

  const label = selected ? format(selected, "EEE, MMM d, yyyy") : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "hos-input flex items-center justify-between gap-2 text-left",
            !selected && "text-[color:var(--text-secondary)]",
            disabled && "opacity-60 cursor-not-allowed",
            className,
          )}
        >
          <span className="truncate">{label}</span>
          <CalendarIcon size={14} className="text-[color:var(--gold)] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-[color:var(--bg-surface)] border-[color:var(--border)]"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          weekStartsOn={weekStartsOn}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
